import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield, AlertTriangle, FileText, ChevronLeft, ChevronRight, Search,
  Building2, ArrowRightLeft, Plus, X, CheckCircle, Eye, TrendingUp,
  TrendingDown, BarChart3, Package, Clock, Users, Lock, Download,
  Wifi, WifiOff, Unlock, Activity, ShieldAlert, RefreshCw
} from 'lucide-react';

const SEVERITY_CONFIG = {
  info: { color: 'text-blue-400 bg-blue-500/20', label: 'Info' },
  warning: { color: 'text-amber-400 bg-amber-500/20', label: 'Attention' },
  critical: { color: 'text-red-400 bg-red-500/20', label: 'Critique' },
};

const AuditCompliancePage = () => {
  const { api, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('audit');

  const tabs = [
    { id: 'audit', label: 'Journal d\'audit', icon: FileText },
    { id: 'fraud', label: 'Anti-fraude', icon: Shield },
    { id: 'security', label: 'Monitoring', icon: ShieldAlert },
    { id: 'boutiques', label: 'Multi-boutiques', icon: Building2 },
    { id: 'analytics', label: 'Analytique', icon: BarChart3 },
    { id: 'compliance', label: 'Conformité', icon: Lock },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in" data-testid="audit-compliance-page">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
            Audit, Sécurité & Conformité
          </h2>
          <p className="text-sm text-gray-400 mt-1">Anti-fraude, multi-boutiques, analytique avancée et conformité légale</p>
        </div>

        <div className="flex gap-1 mb-6 bg-white/5 p-1 rounded-xl overflow-x-auto" data-testid="audit-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                  activeTab === tab.id ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`} data-testid={`audit-tab-${tab.id}`}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'audit' && <AuditTab api={api} />}
        {activeTab === 'fraud' && <FraudTab api={api} />}
        {activeTab === 'security' && <SecurityMonitorTab api={api} />}
        {activeTab === 'boutiques' && <BoutiquesTab api={api} isAdmin={isAdmin} />}
        {activeTab === 'analytics' && <AnalyticsTab api={api} />}
        {activeTab === 'compliance' && <ComplianceTab api={api} />}
      </div>
    </Layout>
  );
};

// ═══ AUDIT LOG TAB ═══
const AuditTab = ({ api }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ action: '', severity: '' });
  const [stats, setStats] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (filters.action) params.append('action', filters.action);
      if (filters.severity) params.append('severity', filters.severity);
      const res = await api.get(`/audit/log?${params}`);
      setLogs(res.data.logs || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api, page, filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { api.get('/audit/stats?days=30').then(r => setStats(r.data)).catch(() => {}); }, [api]);

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-blue-500/10 rounded-xl p-3 border border-white/5"><div className="text-xs text-gray-400">Total (30j)</div><div className="text-xl font-bold text-blue-400">{stats.total}</div></div>
          <div className="bg-blue-500/10 rounded-xl p-3 border border-white/5"><div className="text-xs text-gray-400">Info</div><div className="text-xl font-bold text-blue-400">{stats.by_severity?.info || 0}</div></div>
          <div className="bg-amber-500/10 rounded-xl p-3 border border-white/5"><div className="text-xs text-gray-400">Attention</div><div className="text-xl font-bold text-amber-400">{stats.by_severity?.warning || 0}</div></div>
          <div className="bg-red-500/10 rounded-xl p-3 border border-white/5"><div className="text-xs text-gray-400">Critique</div><div className="text-xl font-bold text-red-400">{stats.by_severity?.critical || 0}</div></div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {['', 'info', 'warning', 'critical'].map(s => (
          <button key={s} onClick={() => { setFilters({...filters, severity: s}); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${filters.severity === s ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>
            {s === '' ? 'Tous' : SEVERITY_CONFIG[s]?.label}
          </button>
        ))}
      </div>

      <div className="glass overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-dark-custom text-sm" data-testid="audit-log-table">
            <thead><tr><th>Date</th><th>Sévérité</th><th>Action</th><th>Type</th><th>Utilisateur</th><th>Détails</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-8"><div className="loading-spinner mx-auto"></div></td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-8 text-gray-400">Aucun log d'audit</td></tr>
              ) : logs.map(log => {
                const sev = SEVERITY_CONFIG[log.severity] || SEVERITY_CONFIG.info;
                return (
                  <tr key={log.id}>
                    <td className="text-xs text-gray-400">{log.created_at ? new Date(log.created_at).toLocaleString('fr-FR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : '-'}</td>
                    <td><span className={`px-2 py-0.5 rounded text-xs font-semibold ${sev.color}`}>{sev.label}</span></td>
                    <td className="font-semibold">{log.action}</td>
                    <td className="text-gray-400 text-xs">{log.entity_type}</td>
                    <td className="text-cyan-400 text-xs">{log.username}</td>
                    <td className="text-xs text-gray-400 max-w-[200px] truncate">{JSON.stringify(log.details || {}).substring(0, 80)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-2">
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page===1} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronLeft className="w-5 h-5"/></button>
          <span className="text-sm">Page {page} / {totalPages} ({total} logs)</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page===totalPages} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronRight className="w-5 h-5"/></button>
        </div>
      )}
    </div>
  );
};

// ═══ SECURITY MONITOR TAB ═══
const SecurityMonitorTab = ({ api }) => {
  const [dashboard, setDashboard] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventFilter, setEventFilter] = useState('');
  const [unbanning, setUnbanning] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/security/dashboard');
      setDashboard(res.data);
    } catch (err) { console.error(err); }
  }, [api]);

  const fetchEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: eventsPage, limit: 20 });
      if (eventFilter) params.append('event_type', eventFilter);
      const res = await api.get(`/security/events?${params}`);
      setEvents(res.data.events || []);
      setEventsTotalPages(res.data.total_pages || 1);
      setEventsTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
  }, [api, eventsPage, eventFilter]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchEvents()]);
      setLoading(false);
    })();
  }, [fetchDashboard, fetchEvents]);

  const handleUnban = async (ip) => {
    setUnbanning(ip);
    try {
      await api.post('/security/unban', { ip });
      await fetchDashboard();
    } catch (err) { alert('Erreur: ' + (err.response?.data?.detail || err.message)); }
    finally { setUnbanning(null); }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchEvents()]);
    setLoading(false);
  };

  const EVENT_LABELS = {
    ip_banned: { color: 'text-red-400 bg-red-500/20', label: 'IP Bannie' },
    ip_unbanned: { color: 'text-emerald-400 bg-emerald-500/20', label: 'IP Débloquée' },
    rate_limit: { color: 'text-amber-400 bg-amber-500/20', label: 'Rate Limit' },
    blocked_request: { color: 'text-red-400 bg-red-500/20', label: 'Bloqué' },
  };

  if (loading) return <div className="text-center py-12"><div className="loading-spinner mx-auto"></div></div>;

  return (
    <div className="space-y-4" data-testid="security-monitor-tab">
      {/* Stats KPIs */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-gray-200 flex items-center gap-2"><Activity className="w-5 h-5 text-cyan-400" /> Monitoring en temps réel</h3>
        <button onClick={handleRefresh} className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm flex items-center gap-2 text-gray-300" data-testid="security-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Actualiser
        </button>
      </div>

      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-red-500/10 rounded-xl p-3 border border-red-500/20">
            <div className="text-xs text-gray-400">IPs bannies actives</div>
            <div className="text-2xl font-bold text-red-400" data-testid="active-bans-count">{dashboard.active_bans_count}</div>
          </div>
          <div className="bg-amber-500/10 rounded-xl p-3 border border-amber-500/20">
            <div className="text-xs text-gray-400">Tentatives en cours</div>
            <div className="text-2xl font-bold text-amber-400">{dashboard.failed_attempts_count}</div>
          </div>
          <div className="bg-cyan-500/10 rounded-xl p-3 border border-white/5">
            <div className="text-xs text-gray-400">Events aujourd'hui</div>
            <div className="text-2xl font-bold text-cyan-400">{dashboard.events_today}</div>
          </div>
          <div className="bg-purple-500/10 rounded-xl p-3 border border-white/5">
            <div className="text-xs text-gray-400">Rate limits (24h)</div>
            <div className="text-2xl font-bold text-purple-400">{dashboard.rate_limits_today}</div>
          </div>
          <div className="bg-blue-500/10 rounded-xl p-3 border border-white/5">
            <div className="text-xs text-gray-400">Events (7j)</div>
            <div className="text-2xl font-bold text-blue-400">{dashboard.events_week}</div>
          </div>
        </div>
      )}

      {/* Active bans */}
      <div className="glass p-4">
        <h4 className="font-bold text-gray-200 mb-3 flex items-center gap-2">
          <WifiOff className="w-4 h-4 text-red-400" /> IPs bannies ({dashboard?.active_bans?.length || 0})
        </h4>
        {!dashboard?.active_bans?.length ? (
          <div className="text-center py-6">
            <Wifi className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Aucune IP bannie actuellement</p>
          </div>
        ) : (
          <div className="space-y-2">
            {dashboard.active_bans.map(ban => (
              <div key={ban.ip} className="flex items-center justify-between bg-red-500/5 border border-red-500/20 rounded-xl px-4 py-3">
                <div>
                  <span className="font-mono text-red-400 font-bold text-sm" data-testid={`banned-ip-${ban.ip}`}>{ban.ip}</span>
                  <div className="text-xs text-gray-400 mt-0.5">
                    Expire dans {Math.ceil(ban.remaining_seconds / 60)} min
                    <span className="ml-2 text-gray-500">({new Date(ban.ban_until).toLocaleString('fr-FR')})</span>
                  </div>
                </div>
                <button
                  onClick={() => handleUnban(ban.ip)}
                  disabled={unbanning === ban.ip}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                  data-testid={`unban-btn-${ban.ip}`}
                >
                  <Unlock className="w-3.5 h-3.5" />
                  {unbanning === ban.ip ? '...' : 'Débloquer'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Failed attempts */}
      {dashboard?.failed_attempts?.length > 0 && (
        <div className="glass p-4">
          <h4 className="font-bold text-gray-200 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Tentatives échouées en cours
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full table-dark-custom text-sm">
              <thead><tr><th>IP</th><th>Tentatives</th><th>Depuis</th><th>Restant avant ban</th></tr></thead>
              <tbody>
                {dashboard.failed_attempts.map(a => (
                  <tr key={a.ip}>
                    <td className="font-mono text-amber-400">{a.ip}</td>
                    <td className="text-center font-bold">{a.count}/15</td>
                    <td className="text-xs text-gray-400">{new Date(a.first_attempt).toLocaleString('fr-FR')}</td>
                    <td className="text-center"><span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400">{15 - a.count} restantes</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Event log */}
      <div className="glass p-4">
        <h4 className="font-bold text-gray-200 mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-cyan-400" /> Journal des événements ({eventsTotal})</h4>
        <div className="flex gap-2 flex-wrap mb-3">
          {['', 'ip_banned', 'ip_unbanned', 'rate_limit', 'blocked_request'].map(t => (
            <button key={t} onClick={() => { setEventFilter(t); setEventsPage(1); }}
              className={`px-3 py-1 rounded-lg text-xs font-semibold ${eventFilter === t ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}
              data-testid={`event-filter-${t || 'all'}`}>
              {t === '' ? 'Tous' : EVENT_LABELS[t]?.label || t}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-dark-custom text-sm" data-testid="security-events-table">
            <thead><tr><th>Date</th><th>Type</th><th>IP</th><th>Détails</th></tr></thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan="4" className="text-center py-6 text-gray-400">Aucun événement</td></tr>
              ) : events.map((ev, i) => {
                const cfg = EVENT_LABELS[ev.type] || { color: 'text-gray-400 bg-white/10', label: ev.type };
                return (
                  <tr key={i}>
                    <td className="text-xs text-gray-400 whitespace-nowrap">{ev.created_at ? new Date(ev.created_at).toLocaleString('fr-FR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit'}) : '-'}</td>
                    <td><span className={`px-2 py-0.5 rounded text-xs font-semibold ${cfg.color}`}>{cfg.label}</span></td>
                    <td className="font-mono text-sm">{ev.ip}</td>
                    <td className="text-xs text-gray-400 max-w-[300px] truncate">{ev.details || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {eventsTotalPages > 1 && (
          <div className="flex items-center justify-center gap-4 py-2 mt-2">
            <button onClick={() => setEventsPage(p => Math.max(1, p-1))} disabled={eventsPage===1} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronLeft className="w-5 h-5"/></button>
            <span className="text-sm">Page {eventsPage} / {eventsTotalPages}</span>
            <button onClick={() => setEventsPage(p => Math.min(eventsTotalPages, p+1))} disabled={eventsPage===eventsTotalPages} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50"><ChevronRight className="w-5 h-5"/></button>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══ ANTI-FRAUD TAB ═══
const FraudTab = ({ api }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/audit/fraud-alerts');
        setAlerts(res.data.alerts || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [api]);

  if (loading) return <div className="text-center py-12"><div className="loading-spinner mx-auto"></div></div>;

  return (
    <div className="space-y-4" data-testid="fraud-alerts-tab">
      <div className="glass p-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-200 flex items-center gap-2"><Shield className="w-5 h-5 text-purple-400" /> Détection d'anomalies</h3>
          <p className="text-xs text-gray-400 mt-1">Analyse automatique des 7 et 30 derniers jours</p>
        </div>
        <span className={`px-4 py-2 rounded-xl text-sm font-bold ${alerts.length === 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
          {alerts.length === 0 ? 'Aucune alerte' : `${alerts.length} alerte(s)`}
        </span>
      </div>

      {alerts.length === 0 ? (
        <div className="glass p-8 text-center">
          <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <p className="text-lg font-semibold text-gray-200">Aucune activité suspecte détectée</p>
          <p className="text-sm text-gray-400 mt-1">Tous les indicateurs sont normaux</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert, i) => {
            const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.warning;
            return (
              <div key={i} className={`glass p-4 border-l-4 ${alert.severity === 'critical' ? 'border-red-500' : 'border-amber-500'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className={`w-4 h-4 ${alert.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`} />
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${sev.color}`}>{sev.label}</span>
                      <span className="text-xs text-gray-400">{alert.type?.replace(/_/g, ' ')}</span>
                    </div>
                    <p className="font-semibold text-gray-200 mt-1">{alert.message}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══ MULTI-BOUTIQUES TAB ═══
const BoutiquesTab = ({ api, isAdmin }) => {
  const [boutiques, setBoutiques] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [newBoutique, setNewBoutique] = useState({ code: '', nom: '', adresse: '', telephone: '' });
  const [transferData, setTransferData] = useState({ reference: '', quantite: 1, boutique_source: 'principal', boutique_destination: '', note: '' });
  const [stockSearch, setStockSearch] = useState('');
  const [stockResults, setStockResults] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, tRes] = await Promise.all([api.get('/boutiques'), api.get('/boutiques/transfers?limit=20')]);
      setBoutiques(bRes.data || []);
      setTransfers(tRes.data.transfers || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const searchStock = async (q) => {
    setStockSearch(q);
    if (q.length < 2) { setStockResults([]); return; }
    try {
      const res = await api.get(`/stock?q=${encodeURIComponent(q)}`);
      setStockResults((res.data || []).slice(0, 8));
    } catch (err) { console.error(err); }
  };

  const handleAddBoutique = async () => {
    if (!newBoutique.code || !newBoutique.nom) return alert('Code et nom requis');
    setSaving(true);
    try {
      await api.post('/boutiques', newBoutique);
      setShowAdd(false);
      setNewBoutique({ code: '', nom: '', adresse: '', telephone: '' });
      fetchData();
    } catch (err) { alert('Erreur: ' + (err.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  const handleTransfer = async () => {
    if (!transferData.reference || !transferData.boutique_destination) return alert('Article et destination requis');
    setSaving(true);
    try {
      await api.post('/boutiques/transfer', { ...transferData, quantite: parseInt(transferData.quantite) });
      setShowTransfer(false);
      setTransferData({ reference: '', quantite: 1, boutique_source: 'principal', boutique_destination: '', note: '' });
      setStockSearch('');
      fetchData();
    } catch (err) { alert('Erreur: ' + (err.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="boutiques-tab">
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <h3 className="font-bold text-gray-200">Boutiques ({boutiques.length})</h3>
        <div className="flex gap-2">
          <button onClick={() => setShowTransfer(true)} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold flex items-center gap-2" data-testid="transfer-btn">
            <ArrowRightLeft className="w-4 h-4" /> Transfert
          </button>
          {isAdmin && (
            <button onClick={() => setShowAdd(true)} className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold flex items-center gap-2" data-testid="add-boutique-btn">
              <Plus className="w-4 h-4" /> Ajouter
            </button>
          )}
        </div>
      </div>

      {loading ? <div className="text-center py-8"><div className="loading-spinner mx-auto"></div></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {boutiques.map(b => (
            <div key={b.id} className="glass p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-white">{b.nom}</div>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${b.actif ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {b.actif ? 'Actif' : 'Inactif'}
                </span>
              </div>
              <div className="text-xs text-gray-400 space-y-1">
                <div>Code: <span className="text-cyan-400 font-mono">{b.code}</span></div>
                {b.adresse && <div>{b.adresse}</div>}
                {b.telephone && <div>{b.telephone}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Transfers history */}
      <div className="glass p-4">
        <h4 className="font-bold text-gray-200 mb-3 flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-purple-400" /> Derniers transferts</h4>
        {transfers.length === 0 ? <p className="text-sm text-gray-400">Aucun transfert</p> : (
          <div className="overflow-x-auto">
            <table className="w-full table-dark-custom text-sm">
              <thead><tr><th>Date</th><th>Article</th><th>Qté</th><th>Source</th><th>Destination</th><th>Par</th></tr></thead>
              <tbody>
                {transfers.map(t => (
                  <tr key={t.id}>
                    <td className="text-xs text-gray-400">{t.created_at ? new Date(t.created_at).toLocaleString('fr-FR', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : '-'}</td>
                    <td className="font-semibold">{t.designation}</td>
                    <td className="text-center font-bold">{t.quantite}</td>
                    <td className="text-red-400 text-xs">{t.boutique_source}</td>
                    <td className="text-emerald-400 text-xs">{t.boutique_destination}</td>
                    <td className="text-xs text-gray-400">{t.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add boutique modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex:9999}}>
          <div className="modal-dark w-full max-w-md">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg">Nouvelle boutique</h3>
              <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-3">
              <div><label className="block text-sm text-gray-300 mb-1">Code</label>
                <input type="text" value={newBoutique.code} onChange={e => setNewBoutique({...newBoutique, code: e.target.value})} className="input-dark w-full py-2 text-sm" placeholder="ex: boutique2" /></div>
              <div><label className="block text-sm text-gray-300 mb-1">Nom</label>
                <input type="text" value={newBoutique.nom} onChange={e => setNewBoutique({...newBoutique, nom: e.target.value})} className="input-dark w-full py-2 text-sm" placeholder="Magasin Centre-ville" /></div>
              <div><label className="block text-sm text-gray-300 mb-1">Adresse</label>
                <input type="text" value={newBoutique.adresse} onChange={e => setNewBoutique({...newBoutique, adresse: e.target.value})} className="input-dark w-full py-2 text-sm" /></div>
              <div><label className="block text-sm text-gray-300 mb-1">Téléphone</label>
                <input type="text" value={newBoutique.telephone} onChange={e => setNewBoutique({...newBoutique, telephone: e.target.value})} className="input-dark w-full py-2 text-sm" /></div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowAdd(false)} className="flex-1 py-2.5 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold text-sm">Annuler</button>
                <button onClick={handleAddBoutique} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm disabled:opacity-50" data-testid="save-boutique-btn">
                  {saving ? '...' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer modal */}
      {showTransfer && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{zIndex:9999}}>
          <div className="modal-dark w-full max-w-md" data-testid="transfer-modal">
            <div className="modal-dark-header flex items-center justify-between">
              <h3 className="font-bold text-lg">Transfert de stock</h3>
              <button onClick={() => setShowTransfer(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Article</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
                  <input type="text" value={stockSearch} onChange={e => searchStock(e.target.value)} className="input-dark w-full py-2 pl-9 text-sm" placeholder="Rechercher..."/>
                </div>
                {stockResults.length > 0 && (
                  <div className="mt-1 max-h-32 overflow-y-auto border border-white/10 rounded-lg">
                    {stockResults.map(s => (
                      <button key={s.reference || s.id} onClick={() => { setTransferData({...transferData, reference: s.reference}); setStockSearch(s.designation); setStockResults([]); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-white/10">{s.designation} <span className="text-xs text-gray-400">({s.reference}) Stock: {s.quantite}</span></button>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm text-gray-300 mb-1">Source</label>
                  <select value={transferData.boutique_source} onChange={e => setTransferData({...transferData, boutique_source: e.target.value})} className="input-dark w-full py-2 text-sm">
                    {boutiques.map(b => <option key={b.code} value={b.code}>{b.nom}</option>)}
                  </select></div>
                <div><label className="block text-sm text-gray-300 mb-1">Destination</label>
                  <select value={transferData.boutique_destination} onChange={e => setTransferData({...transferData, boutique_destination: e.target.value})} className="input-dark w-full py-2 text-sm">
                    <option value="">Choisir...</option>
                    {boutiques.filter(b => b.code !== transferData.boutique_source).map(b => <option key={b.code} value={b.code}>{b.nom}</option>)}
                  </select></div>
              </div>
              <div><label className="block text-sm text-gray-300 mb-1">Quantité</label>
                <input type="number" value={transferData.quantite} onChange={e => setTransferData({...transferData, quantite: e.target.value})} className="input-dark w-full py-2 text-sm" min="1"/></div>
              <div><label className="block text-sm text-gray-300 mb-1">Note</label>
                <input type="text" value={transferData.note} onChange={e => setTransferData({...transferData, note: e.target.value})} className="input-dark w-full py-2 text-sm" placeholder="Raison du transfert"/></div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowTransfer(false)} className="flex-1 py-2.5 rounded-xl bg-gray-700 hover:bg-gray-600 font-bold text-sm">Annuler</button>
                <button onClick={handleTransfer} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm disabled:opacity-50" data-testid="execute-transfer-btn">
                  {saving ? '...' : 'Transférer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══ ANALYTICS TAB ═══
const AnalyticsTab = ({ api }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/stock-analytics');
        setData(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [api]);

  if (loading) return <div className="text-center py-12"><div className="loading-spinner mx-auto"></div></div>;
  if (!data) return <p className="text-gray-400 text-center">Erreur</p>;

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Rotation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-cyan-500/10 rounded-xl p-4 border border-white/5">
          <div className="text-xs text-gray-400">Taux de rotation</div>
          <div className="text-2xl font-bold text-cyan-400">{data.rotation?.taux_rotation || 0}%</div>
          <div className="text-xs text-gray-400">{data.rotation?.active_articles || 0} / {data.rotation?.total_articles || 0} articles actifs (90j)</div>
        </div>
        <div className="bg-amber-500/10 rounded-xl p-4 border border-white/5">
          <div className="text-xs text-gray-400">Produits dormants</div>
          <div className="text-2xl font-bold text-amber-400">{data.dormant?.total_count || 0}</div>
          <div className="text-xs text-gray-400">Valeur immobilisée: {(data.dormant?.valeur_immobilisee || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2})} €</div>
        </div>
        <div className="bg-emerald-500/10 rounded-xl p-4 border border-white/5">
          <div className="text-xs text-gray-400">À réapprovisionner</div>
          <div className="text-2xl font-bold text-emerald-400">{data.reorder_suggestions?.length || 0}</div>
          <div className="text-xs text-gray-400">Articles sous seuil avec ventes récentes</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top sold */}
        <div className="glass p-4">
          <h4 className="font-bold text-gray-200 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400"/> Top ventes (30j)</h4>
          {data.top_sold?.length > 0 ? (
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {data.top_sold.slice(0, 10).map((item, i) => (
                <div key={i} className="flex justify-between text-sm bg-white/5 px-3 py-1.5 rounded">
                  <span className="truncate flex-1">{item.designation}</span>
                  <span className="text-emerald-400 font-bold ml-2">{item.quantite_vendue}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-gray-500">Aucune donnée</p>}
        </div>

        {/* Top purchased */}
        <div className="glass p-4">
          <h4 className="font-bold text-gray-200 mb-3 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-blue-400"/> Top achats (30j)</h4>
          {data.top_purchased?.length > 0 ? (
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {data.top_purchased.slice(0, 10).map((item, i) => (
                <div key={i} className="flex justify-between text-sm bg-white/5 px-3 py-1.5 rounded">
                  <span className="truncate flex-1">{item.designation}</span>
                  <span className="text-blue-400 font-bold ml-2">{item.quantite_achetee}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-gray-500">Aucune donnée</p>}
        </div>
      </div>

      {/* Reorder suggestions */}
      {data.reorder_suggestions?.length > 0 && (
        <div className="glass p-4">
          <h4 className="font-bold text-gray-200 mb-3 flex items-center gap-2"><Package className="w-4 h-4 text-emerald-400"/> Suggestions de réapprovisionnement</h4>
          <div className="overflow-x-auto">
            <table className="w-full table-dark-custom text-sm">
              <thead><tr><th>Article</th><th>Stock</th><th>Seuil</th><th>Ventes 30j</th><th>Qté suggérée</th><th>Coût estimé</th></tr></thead>
              <tbody>
                {data.reorder_suggestions.map((item, i) => (
                  <tr key={i}>
                    <td className="font-semibold max-w-[200px] truncate">{item.designation}</td>
                    <td className={`text-center font-bold ${item.quantite <= 0 ? 'text-red-400' : 'text-amber-400'}`}>{item.quantite}</td>
                    <td className="text-center text-gray-400">{item.seuil_alerte}</td>
                    <td className="text-center text-emerald-400 font-bold">{item.ventes_30j}</td>
                    <td className="text-center text-cyan-400 font-bold">{item.quantite_suggeree}</td>
                    <td className="text-right text-gray-300">{item.cout_estime?.toLocaleString('fr-FR', {minimumFractionDigits:2})} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="glass p-4">
        <h4 className="font-bold text-gray-200 mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-purple-400"/> Répartition par catégorie</h4>
        <div className="overflow-x-auto">
          <table className="w-full table-dark-custom text-sm">
            <thead><tr><th>Catégorie</th><th>Articles</th><th>Quantité</th><th>Valeur vente</th><th>Valeur achat</th><th>Marge</th></tr></thead>
            <tbody>
              {data.categories?.map((cat, i) => (
                <tr key={i}>
                  <td className="font-semibold">{cat.categorie}</td>
                  <td className="text-center">{cat.articles}</td>
                  <td className="text-center">{cat.quantite}</td>
                  <td className="text-right text-emerald-400">{cat.valeur_vente?.toLocaleString('fr-FR', {minimumFractionDigits:2})} €</td>
                  <td className="text-right text-gray-400">{cat.valeur_achat?.toLocaleString('fr-FR', {minimumFractionDigits:2})} €</td>
                  <td className="text-right text-amber-400 font-bold">{(cat.valeur_vente - cat.valeur_achat).toLocaleString('fr-FR', {minimumFractionDigits:2})} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dormant products */}
      {data.dormant?.products?.length > 0 && (
        <div className="glass p-4">
          <h4 className="font-bold text-gray-200 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400"/> Produits dormants (aucun mouvement en 90j)</h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.dormant.products.map((p, i) => (
              <div key={i} className="flex justify-between text-sm bg-white/5 px-3 py-1.5 rounded">
                <span className="truncate">{p.designation}</span>
                <span className="text-amber-400 ml-2">Stock: {p.quantite}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══ COMPLIANCE TAB ═══
const ComplianceTab = ({ api }) => {
  const [integrity, setIntegrity] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/compliance/integrity-check');
        setIntegrity(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [api]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateDebut) params.append('date_debut', dateDebut);
      if (dateFin) params.append('date_fin', dateFin);
      const res = await api.get(`/compliance/export?${params}`);
      setExportData(res.data);
    } catch (err) { alert('Erreur: ' + (err.response?.data?.detail || err.message)); }
  };

  if (loading) return <div className="text-center py-12"><div className="loading-spinner mx-auto"></div></div>;

  return (
    <div className="space-y-6" data-testid="compliance-tab">
      {/* Integrity check */}
      <div className="glass p-6">
        <h3 className="font-bold text-gray-200 mb-4 flex items-center gap-2"><Lock className="w-5 h-5 text-purple-400"/> Vérification d'intégrité</h3>
        {integrity && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white/5 rounded-xl p-3"><div className="text-xs text-gray-400">Total logs</div><div className="text-lg font-bold text-cyan-400">{integrity.total_logs}</div></div>
            <div className="bg-white/5 rounded-xl p-3"><div className="text-xs text-gray-400">Échantillon vérifié</div><div className="text-lg font-bold text-blue-400">{integrity.sample_size}</div></div>
            <div className="bg-white/5 rounded-xl p-3"><div className="text-xs text-gray-400">Valides</div><div className="text-lg font-bold text-emerald-400">{integrity.valid}</div></div>
            <div className={`rounded-xl p-3 ${integrity.integrity === 'OK' ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              <div className="text-xs text-gray-400">Intégrité</div>
              <div className={`text-lg font-bold ${integrity.integrity === 'OK' ? 'text-emerald-400' : 'text-red-400'}`}>
                {integrity.integrity === 'OK' ? 'Intègre' : 'Compromis'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compliance info */}
      <div className="glass p-6">
        <h3 className="font-bold text-gray-200 mb-4">Conformité légale française</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { title: 'Inaltérabilité', desc: 'Les logs d\'audit sont protégés par hash SHA-256. Aucune suppression possible.', ok: true },
            { title: 'Traçabilité', desc: 'Toutes les actions sont enregistrées : utilisateur, date, heure, IP, détails.', ok: true },
            { title: 'Archivage', desc: 'Sessions d\'inventaire non modifiables après validation. Conservation permanente.', ok: true },
            { title: 'Horodatage', desc: 'Toutes les opérations sont horodatées en UTC (ISO 8601).', ok: true },
          ].map((item, i) => (
            <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/5">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span className="font-semibold text-gray-200">{item.title}</span>
              </div>
              <p className="text-xs text-gray-400">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Export */}
      <div className="glass p-6">
        <h3 className="font-bold text-gray-200 mb-4 flex items-center gap-2"><Download className="w-5 h-5 text-purple-400"/> Export de conformité</h3>
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div><label className="block text-xs text-gray-400 mb-1">Date début</label>
            <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="input-dark py-2 text-sm" /></div>
          <div><label className="block text-xs text-gray-400 mb-1">Date fin</label>
            <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="input-dark py-2 text-sm" /></div>
          <button onClick={handleExport} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold" data-testid="export-compliance-btn">
            Générer rapport
          </button>
        </div>

        {exportData && (
          <div className="bg-white/5 rounded-xl p-4 border border-white/5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-gray-400">Mouvements stock:</span> <span className="font-bold text-cyan-400">{exportData.mouvements_stock}</span></div>
              <div><span className="text-gray-400">Journal audit:</span> <span className="font-bold text-blue-400">{exportData.journal_audit}</span></div>
              <div><span className="text-gray-400">Ventes:</span> <span className="font-bold text-emerald-400">{exportData.ventes}</span></div>
              <div><span className="text-gray-400">Inventaires:</span> <span className="font-bold text-amber-400">{exportData.inventaires}</span></div>
            </div>
            <div className="text-xs text-gray-400 mt-3">Généré le {new Date(exportData.generated_at).toLocaleString('fr-FR')} par {exportData.generated_by}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditCompliancePage;
